import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { Link } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { observer } from "mobx-react-lite"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Input } from "@/components/Input/Input.tsx"
import { Principal } from "@/components/Principal.tsx"
import { ProjectSettingsDialog } from "@/components/ProjectSettingsDialog/ProjectSettingsDialog.tsx"
import { TabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useProjectLocation } from "@/hooks/useMatterLocation.ts"
import { useMediaQuery } from "@/hooks/useMediaQuery.ts"
import { useMyPrincipal } from "@/hooks/useProfile.ts"
import { AlertFill, EditLight, Heart20Green, Icon16, Icon20 } from "@/icons/Icons.ts"
import { formatDateTime } from "@/lib/date.ts"
import { branchDisplayName, formatTaskTitle, projectDisplayName } from "@/lib/displayUtils.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId, useCurrentProject } from "@/routes/projects/projectHooks.ts"
import { ProjectAuthErrorPopup } from "@/routes/tasks/TaskViewScene/ProjectAuthErrorPopup.tsx"
import { focusOutline, mobileBreakpoint } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { Characters, smallFontSize } from "@/styles/textStyles.ts"

import type { ProjectSettingsTabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import type { AiAgentTask } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { KeyboardEvent } from "react"


export const Breadcrumbs = observer((props: { model: TaskViewModel }) => {
  const isMobile = useMediaQuery(mobileBreakpoint)
  const initProjectStep = props.model.initProjectStep.get()
  const breadcrumbs = props.model.configurationTaskBreadcrumbs.get()

  const orgId = useCurrentOrgId()

  if (initProjectStep === "EnterUrl" || initProjectStep === "EnterProjectName") {
    return null
  }

  const projectName = props.model.projectName.get()

  if (props.model.isConfigurationTask.get() && breadcrumbs) {
    return (
      <WrappingContainer>
        {props.model.project.get().initProjectTaskId === props.model.taskId
          ? (
            <>
              <span>
                Setup {projectDisplayName(projectName)}{!props.model.taskSettings.get()?.generateGuidelinesInOnboarding ? `:${Characters.Nbsp}` : ""}
              </span>
              {
                !props.model.taskSettings.get()?.generateGuidelinesInOnboarding && breadcrumbs.map((it, index) => (
                  <Item
                    key={it.name}
                    active={it.active}
                    className={index < breadcrumbs.length - 1 ? ArrowSeparator : undefined}
                  >
                    {it.name}
                  </Item>
                ))
              }
            </>
          ) : (
            <span>
              <Link {...Navigator.project(orgId, props.model.project.get().key)} viewTransition>
                {projectDisplayName(projectName)}
              </Link> setup
            </span>
          )
        }
      </WrappingContainer>
    )
  }

  if (isMobile) {
    return (
      <Container>
        <CurrentTaskItem model={props.model} />
      </Container>
    )
  }

  return (
    <Container>
      <ProjectItem model={props.model} />
      <CurrentTaskItem model={props.model} />
    </Container>
  )
})

const ProjectItem = observer(({ model }: { model: TaskViewModel }) => {
  const project = useCurrentProject()
  const location = useProjectLocation(project.key)
  const hasAuthError = model.repoSyncStatus.get()?.authError === true
  const canEditProject = model.canEditProject.get()

  const [authErrorPopupOpen, setAuthErrorPopupOpen] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [settingsInitialTab, setSettingsInitialTab] = useState<ProjectSettingsTabId | undefined>(undefined)

  return (
    <Item className={Separator}>
      <BreadcrumbLinkContainer inline>
        <Ellipsis>
          <Horizontal gap="4px" alignItems="center">
            <BreadcrumbLink {...location} viewTransition data-testid="breadcrumbs-project-link">
              <span>{project.name}</span>
            </BreadcrumbLink>
            {hasAuthError && (
              <ProjectAuthErrorPopup
                isOpen={authErrorPopupOpen}
                onOpenChange={setAuthErrorPopupOpen}
                canEditProject={canEditProject}
                goToSettings={() => {
                  setSettingsInitialTab(TabId.Git)
                  setSettingsDialogOpen(true)
                }}
              >
                <Button kind="icon" size="small">
                  <AlertFill className={Icon16} color="var(--error-color)" />
                </Button>
              </ProjectAuthErrorPopup>
            )}
          </Horizontal>
        </Ellipsis>
      </BreadcrumbLinkContainer>


      <ProjectSettingsDialog
        project={project}
        dialogOpen={settingsDialogOpen}
        setDialogOpen={(open) => {
          setSettingsDialogOpen(open)
          if (!open) setSettingsInitialTab(undefined)
        }}
        initialTab={settingsInitialTab}
      />
    </Item>
  )
})

const CurrentTaskItem = observer(({ model }: { model: TaskViewModel }) => {
  const task = model.task.get()

  return task ? <EditableTaskItem model={model} /> : null
})

const EditableTaskItem = ({ model }: { model: TaskViewModel }) => {
  const myPrincipal = useMyPrincipal()

  const [tooltipOpen, setTooltipOpen] = useState(false)

  const [isHovered, setIsHovered] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const task = model.task.get()!
  const taskName = formatTaskTitle(task.title)

  const handleEditClick = useCallback(() => {
    if (!taskName) return
    setIsEditing(true)
    setTooltipOpen(false)
    setEditedName(taskName)
  }, [taskName])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleSave = useCallback(async () => {
    if (!editedName.trim() || editedName === taskName) {
      setIsEditing(false)
      return
    }

    await model.updateTaskTitle(editedName)
    setIsEditing(false)
  }, [model, editedName, taskName])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      void handleSave()
    } else if (e.key === "Escape") {
      setIsEditing(false)
    }
  }, [handleSave])

  const handleBlur = useCallback(() => {
    setIsEditing(false)
  }, [])

  if (isEditing) {
    return (
      <Item active>
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.15, ease: "easeOut" }}
        >
          <EditingInput
            ref={inputRef}
            size={editedName.length + 1}
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            data-testid="task-title-edit-input"
          />
        </motion.div>
      </Item>
    )
  }

  return (
    <Item active>
      <TaskNameWrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid="breadcrumbs-task-title"
      >
        <BreadcrumbLinkContainer inline>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen} content={(
            <TaskNameTooltip task={task} />
          )} ignoreEllipsis>
            <Ellipsis>
              <span>
                {formatTaskTitle(task.title)}
              </span>
            </Ellipsis>
          </Tooltip>
        </BreadcrumbLinkContainer>
        {model.successful.get() && (
          <Tooltip content="Marked as successful">
            <Heart20Green />
          </Tooltip>
        )}
        {task.createdBy.id === myPrincipal.id && (
          <Tooltip content="Edit title">
            <EditIconButton
              kind="icon"
              data-testid="edit-task-title-icon"
              onClick={handleEditClick}
              isHovered={isHovered}
            >
              <EditLight className={Icon20} />
            </EditIconButton>
          </Tooltip>
        )}
      </TaskNameWrapper>
    </Item>
  )
}

const TaskNameTooltip = ({ task }: {
  task: AiAgentTask
}) => {
  return (
    <TaskTooltipContent>
      {formatTaskTitle(task.title)}
      <TaskMetadata>
        Created by{Characters.Nbsp}
        <Principal principal={task.createdBy} />
        ,{Characters.Nbsp}{formatDateTime(task.createdAt)}
        <BranchName name={task.initialBranch} />
      </TaskMetadata>
    </TaskTooltipContent>
  )
}

const TaskNameTooltipSeparator = `${Characters.Nbsp}|${Characters.Nbsp}`

function BranchName(props: {
  name: string | null
}) {
  return props.name && (TaskNameTooltipSeparator + "Branch: " + branchDisplayName(props.name))
}

const Container = styled(Horizontal)`
  ${textStyles.smallFontSize};

  color: var(--secondary-text-color);

  a {
    color: var(--secondary-text-color);
    transition: color 0.2s ease-in-out;

    &:hover {
      color: var(--text-color);
    }
  }
`

const WrappingContainer = styled(Container)`
  flex-wrap: wrap;
`

const Item = styled(Horizontal)<{
  active?: boolean,
  clickable?: boolean
}>`
  color: ${props => props.active ? "var(--text-color)" : "var(--secondary-text-color)"};

  cursor: ${props => props.clickable ? "pointer" : "auto"};

  min-width: ${props => props.active ? "auto" : "max-content"};
`

const Separator = css`
  &::after {
    content: "/";
    margin: 0 6px;
  }
`

const ArrowSeparator = css`
  &::after {
    content: "→";
    margin: 0 6px;
  }
`

const BreadcrumbLinkContainer = styled(EllipsisContainer)`
  &:focus-within:has(:focus-visible) {
    ${focusOutline};
  }
`

const BreadcrumbLink = styled(Link)`
  outline: none;
`

const TaskTooltipContent = styled(Vertical)`
  gap: 2px;

  ${textStyles.smallFontSize};
`

const TaskMetadata = styled(SecondaryTextSpan)`
  font-weight: normal;
  white-space: nowrap;
`

const TaskNameWrapper = styled(Horizontal)`
  align-items: center;
  gap: 4px;
`

const EditIconButton = styled(Button)<{ isHovered: boolean }>`
  visibility: ${props => props.isHovered ? "visible" : "hidden"};

  width: 20px;
  height: 20px;
`

const EditingInput = styled(Input)`
  position: relative;
  left: -11px;

  width: 100%;
  max-width: var(--chat-width);

  height: auto;
  margin: -6px 0;
  padding: 0 4px 0 10px;

  ${smallFontSize};
`
